import React, {useCallback, useEffect, useState} from 'react';
import {
    Container,
    IconButton,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Divider,
    TextField,
} from "@mui/material";
import {Download} from '@mui/icons-material';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';

import services from 'src/services/Services';
import WidgetCustomLoader from 'src/widgets/WidgetCustomLoader';
import WidgetTable from 'src/widgets/WidgetTable';
import dayjs from 'dayjs';

const InternalOrders = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const [openModal, setOpenModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const [search, setSearch] = useState("");
    const [fechaInicio, setFechaInicio] = useState(null);
    const [fechaFin, setFechaFin] = useState(null);

    const articleColumns = [
        {
            field: "Articulo",
            header: "Artículo",
        },
        {
            field: "DescripcionArticulo",
            header: "Descripción",
        },
        {
            field: "Cantidad",
            header: "Cantidad",
        },
        {
            field: "Precio",
            header: "Precio",
            render: (row) =>
                row.Precio?.toLocaleString("es-MX",
                    {style: "currency", currency: "MXN",}),
        },
        {
            field: "PrecioTotal",
            header: "Total",
            render: (row) =>
                row.PrecioTotal?.toLocaleString("es-MX",
                    {style: "currency", currency: "MXN",}),
        },
    ];


    const fetchData = useCallback(async (payload) => {
        try {
            setLoading(true);
            const response = await services.getPedidosInternos(payload);
            setData(response?.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const inicio = dayjs().subtract(20, 'day');
        const fin = dayjs();

        setFechaInicio(inicio);
        setFechaFin(fin);

        fetchData({
            search: "",
            fechaInicio: inicio.format('YYYY-MM-DD'),
            fechaFin: fin.format('YYYY-MM-DD'),
        });
    }, [fetchData]);


    const handleBuscar = () => {
        if (search.trim() !== "") {
            fetchData({
                search,
                fechaInicio: null,
                fechaFin: null,
            });
            return;
        }

        const inicio = fechaInicio ?? dayjs().subtract(20, 'day');
        const fin = fechaFin ?? dayjs();

        setFechaInicio(inicio);
        setFechaFin(fin);

        fetchData({
            search: "",
            fechaInicio: inicio.format('YYYY-MM-DD'),
            fechaFin: fin.format('YYYY-MM-DD'),
        });
    };


    const handleFechaInicioChange = (value) => {
        setSearch("");
        setFechaInicio(value);
    };

    const handleFechaFinChange = (value) => {
        setSearch("");
        setFechaFin(value);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);

        if (value.trim() !== "") {
            setFechaInicio(null);
            setFechaFin(null);
        }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            handleBuscar();
        }
    };

    const formatFechaMX = (fecha) => {
        if (!fecha) return "-";
        const date = new Date(fecha);
        return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("es-MX");
    };
    /* =========================
       MODAL
    ========================= */
    const handleOpenModal = (row) => {
        setSelectedOrder(row);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedOrder(null);
    };

    /* =========================
       DOWNLOAD
    ========================= */
    const handleDownload = (base64, filename, mimeType) => {
        if (!base64) return;

        const byteCharacters = atob(base64);
        const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));
        const blob = new Blob([new Uint8Array(byteNumbers)], {type: mimeType});

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(link);
    };

    /* =========================
       COLUMNAS
    ========================= */
    const orderColumns = [
        {
            field: "ID",
            header: "ID",
        },
        {
            field: "OrdenCompra",
            header: "Orden de Compra"
        },
        {
            field: "Referencia",
            header: "Referencia",
        }, {
            field: "FechaEmision",
            header: "Fecha emisión",
            render: (row) => row.FechaEmision ? dayjs(row.FechaEmision.split("T")[0]).format('DD-MM-YYYY') : "-"
        }, {field: "Estatus", header: "Estatus",}, {
            field: "Importe",
            header: "Importe",
            render: (row) => row.Importe?.toLocaleString("es-MX", {style: "currency", currency: "MXN",}),
        }, {
            field: "Impuestos",
            header: "Impuestos",
            render: (row) => row.Impuestos?.toLocaleString("es-MX", {style: "currency", currency: "MXN",}),
        }, {
            field: "Total",
            header: "Total",
            render: (row) => row.Total?.toLocaleString("es-MX", {style: "currency", currency: "MXN",}),
        }, {field: "Moneda", header: "Moneda",}, {
            field: "UUID",
            header: "UUID",
            maxWidth: 220,
            render: (row) => row.UUID || "-",
        }, {
            field: "EmbarqueFechaEnvio",
            header: "Envío",
            render: (row) => row.EmbarqueFechaEnvio && row.EmbarqueFechaEnvio !== "0001-01-01T00:00:00Z" ? new Date(row.EmbarqueFechaEnvio).toLocaleDateString("es-MX") : "-"
        }, {
            field: "EmbarqueFechaLlegada",
            header: "Llegada",
            render: (row) => row.EmbarqueFechaLlegada && row.EmbarqueFechaLlegada !== "0001-01-01T00:00:00Z" ? new Date(row.EmbarqueFechaLlegada).toLocaleDateString("es-MX") : "-"
        }, {
            field: "EmbarquePersona",
            header: "Recibió",
            render: (row) => row.EmbarquePersona || "-",
        }, {
            field: "PDFBinario",
            header: "PDF",
            sortable: false,
            render: (row) => (<IconButton size="small" onClick={(e) => {
                e.stopPropagation();
                handleDownload( row.PDFBinario, `pdf-${row.ID}.pdf`, "application/pdf" );
            }} disabled={!row.PDFBinario}> <Download fontSize="small"/> </IconButton>),
        }, {
            field: "XMLBinario",
            header: "XML",
            sortable: false,
            render: (row) => (<IconButton size="small" onClick={(e) => {
                e.stopPropagation();
                handleDownload(row.XMLBinario, `xml - ${row.ID}.xml`, "application/xml");
            }} disabled={!row.XMLBinario}> <Download fontSize="small"/> </IconButton>),
        }, {
            field: "AcuseBinario",
            header: "Acuse",
            sortable: false,
            render: (row) => (<IconButton size="small" onClick={(e) => {
                e.stopPropagation();
                handleDownload(row.AcuseBinario, `acuse - ${row.ID}.pdf`, "application/pdf")
            }} disabled={!row.AcuseBinario}> <Download fontSize="small"/> </IconButton>),
        },];

    /* =========================
       RENDER
    ========================= */
    return (
        <Container maxWidth="lg" sx={{mt: 4}}>
            {alert.open && (
                <Alert
                    severity={alert.severity}
                    onClose={() => setAlert({...alert, open: false})}
                    sx={{mb: 2}}
                >
                    {alert.message}
                </Alert>
            )}

            <Typography variant="h4" sx={{mb: 2}}>
                Pedidos Internos
            </Typography>

            <Grid container spacing={2} alignItems="center">
                {/* SEARCH */}
                <Grid item xs={12} md={5}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Buscar por Orden o Referencia"
                        value={search}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                    />
                </Grid>

                {/* FECHAS */}
                <Grid item xs={12} md={3}>
                    <DatePicker
                        label="Fecha inicio"
                        value={fechaInicio}
                        onChange={handleFechaInicioChange}
                        disabled={Boolean(search)}
                    />
                </Grid>

                <Grid item xs={12} md={3}>
                    <DatePicker
                        label="Fecha fin"
                        value={fechaFin}
                        onChange={handleFechaFinChange}
                        disabled={Boolean(search)}
                        maxDate={dayjs()}
                    />
                </Grid>

                {/* BOTÓN */}
                <Grid item xs={12} md={1}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleBuscar}
                    >
                        Buscar
                    </Button>
                </Grid>
            </Grid>

            <Divider sx={{my: 3}}/>

            {loading ? (
                <WidgetCustomLoader/>
            ) : (
                <WidgetTable
                    columns={orderColumns}
                    rows={data}
                    getRowId={(row) => row.ID}
                    onRowClick={handleOpenModal}
                    enableSearch={false}
                />
            )}

            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
                <DialogTitle>
                    Detalle del pedido {selectedOrder?.FolioVanti}
                </DialogTitle>
                <DialogContent>
                    {selectedOrder && (
                        <>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Fecha:</strong>{" "} {formatFechaMX(selectedOrder?.FechaEmision)}
                                    </Typography> <Typography variant="body2">
                                    <strong>Estatus:</strong>
                                    {" "} {selectedOrder.Estatus}
                                </Typography>
                                </Grid>
                            </Grid>
                            <Divider sx={{my: 2}}/>
                            <Typography variant="subtitle1" sx={{mb: 1}}>
                                Artículos
                            </Typography>
                            <WidgetTable
                                columns={articleColumns}
                                rows={selectedOrder.Articulos || []}
                                emptyMessage="Este pedido no tiene artículos"/> </>)}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default InternalOrders;
